export interface IHandTileHitTarget<TValue> {
  readonly value: TValue;
  readonly x: number;
  readonly y: number;
  readonly halfWidth: number;
  readonly halfHeight: number;
  readonly visible: boolean;
}

export interface IHandTilePointerFallbackInput {
  readonly canSelect: boolean;
  readonly hasPendingSelection: boolean;
}

export interface INativeHandPointerInput {
  readonly clientX: number;
  readonly clientY: number;
  readonly canvasLeft: number;
  readonly canvasTop: number;
  readonly canvasWidth: number;
  readonly canvasHeight: number;
  readonly worldWidth: number;
  readonly worldHeight: number;
}

export interface INativeHandPointerFallbackDeps<TValue> {
  readonly canSelect: () => boolean;
  readonly hasPendingSelection: () => boolean;
  readonly hitTest: (worldX: number, worldY: number) => TValue | null;
  readonly onActivate: (value: TValue, worldX: number, worldY: number) => void;
}

/** scene fallback 只避開同一次點擊已產生的升起牌，不保留跨 pointerdown 的狀態。 */
export const shouldHandleHandTilePointerFallback = ({
  canSelect,
  hasPendingSelection,
}: IHandTilePointerFallbackInput): boolean => canSelect && !hasPendingSelection;

/**
 * Phaser Input Manager 漏掉整次 POINTER_DOWN 時的最後一道保險。
 * 直接接 Canvas 原生 pointerdown，換算成遊戲世界座標後沿用同一套手牌 hit test。
 */
export class CNativeHandPointerFallback<TValue> {
  constructor(private readonly deps: INativeHandPointerFallbackDeps<TValue>) {}

  handle(input: INativeHandPointerInput): boolean {
    if (
      !shouldHandleHandTilePointerFallback({
        canSelect: this.deps.canSelect(),
        hasPendingSelection: this.deps.hasPendingSelection(),
      })
    )
      return false;
    if (input.canvasWidth <= 0 || input.canvasHeight <= 0) return false;

    const worldX = ((input.clientX - input.canvasLeft) / input.canvasWidth) * input.worldWidth;
    const worldY = ((input.clientY - input.canvasTop) / input.canvasHeight) * input.worldHeight;
    if (worldX < 0 || worldX > input.worldWidth || worldY < 0 || worldY > input.worldHeight) {
      return false;
    }

    const value = this.deps.hitTest(worldX, worldY);
    if (value === null) return false;
    this.deps.onActivate(value, worldX, worldY);
    return true;
  }
}

/** 從最上層往下找出包含指定畫面座標的可見手牌。 */
export const findHandTileAtPoint = <TValue>(
  x: number,
  y: number,
  targets: readonly IHandTileHitTarget<TValue>[]
): TValue | null => {
  for (let i = targets.length - 1; i >= 0; i--) {
    const target = targets[i]!;
    if (!target.visible) continue;
    if (Math.abs(x - target.x) > target.halfWidth) continue;
    if (Math.abs(y - target.y) > target.halfHeight) continue;
    return target.value;
  }
  return null;
};
